import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import DropZone from "../components/DropZone";
import { Save, Plus, Trash2, MessageCircle, Bot, Clock, Phone, ArrowUp, ArrowDown } from "lucide-react";

/**
 * Chatbot pré-atendimento — configuração do balão flutuante estilo Emmy.
 * Salva no KV key "chatbot_config".
 */

const DEFAULT_CONFIG = {
  enabled: true,
  delay_seconds: 20,
  attendant_name: "Marina",
  attendant_role: "Consultora Places",
  attendant_avatar: "",
  greeting: "Posso te ajudar?",
  intro_message: "Oi! Sou a Marina, do time da Places. Estou aqui pra entender rapidinho o seu projeto e te conectar com o consultor certo.",
  script: [
    { question: "Como você se chama?", field: "name", placeholder: "Seu nome" },
    { question: "Perfeito, {name}! Qual seu WhatsApp?", field: "phone", placeholder: "(11) 99999-9999" },
  ],
  system_prompt: "",
  whatsapp_number: "5511999999999",
  whatsapp_message_template: "Olá! Vim pelo site da Places. Meu nome é {name}, meu telefone é {phone}. Interesse: {interest}",
  cta_label: "Falar com um consultor no WhatsApp",
  cta_after_turns: 3,
  hide_on_paths: ["/contato", "/pt-br/contato", "/pt-pt/contacto", "/es-py/contacto"],
};

function resolveSrc(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/media/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

export default function ChatbotConfigEditor() {
  const [cfg, setCfg] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content/kv/chatbot_config").then(({ data }) => {
      if (data && typeof data === "object") setCfg({ ...DEFAULT_CONFIG, ...data });
    }).finally(() => setLoading(false));
  }, []);

  const upd = (k, v) => setCfg((s) => ({ ...s, [k]: v }));

  const addScriptStep = () => upd("script", [...(cfg.script || []), { question: "", field: "custom", placeholder: "" }]);
  const removeScriptStep = (i) => upd("script", cfg.script.filter((_, j) => j !== i));
  const moveScriptStep = (i, dir) => {
    const arr = [...cfg.script];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    upd("script", arr);
  };
  const updScriptStep = (i, k, v) => upd("script", cfg.script.map((s, j) => j === i ? { ...s, [k]: v } : s));

  const updPaths = (str) => upd("hide_on_paths", str.split(",").map((s) => s.trim()).filter(Boolean));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content/kv/chatbot_config", { value: cfg });
      toast.success("Configurações do chatbot salvas.");
    } catch { toast.error("Erro ao salvar."); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-neutral-500">Carregando…</div>;

  return (
    <div>
      <div className="flex justify-between items-end mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-accent-orange font-bold">
            <MessageCircle className="w-3 h-3" /> Pré-atendimento
          </div>
          <h1 className="font-display text-4xl">Chatbot Marina</h1>
          <p className="text-sm text-neutral-500 mt-1 max-w-2xl">Balão flutuante estilo Emmy: aparece após X segundos, coleta nome + telefone, conversa via IA e encaminha para o WhatsApp de um consultor.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="chatbot-save"
          className="bg-black text-white text-sm px-5 py-2.5 rounded-full hover:bg-accent-orange transition-colors disabled:opacity-50">
          <Save className="w-3 h-3 inline mr-1" /> {saving ? "Salvando…" : "Salvar"}
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          {/* Ativação + tempo */}
          <section className="border border-black/10 rounded-md p-5">
            <div className="text-xs uppercase tracking-widest text-accent-orange font-bold mb-2">Ativação</div>
            <div className="grid md:grid-cols-2 gap-4">
              <label className="flex items-center gap-3 border border-black/15 rounded-md px-4 py-3 cursor-pointer">
                <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => upd("enabled", e.target.checked)} data-testid="chatbot-enabled" />
                <span className="text-sm">Chatbot ativo no site público</span>
              </label>
              <label className="flex items-center gap-2 border border-black/15 rounded-md px-4 py-3">
                <Clock className="w-4 h-4 text-neutral-500" />
                <span className="text-sm text-neutral-500">Tempo até aparecer:</span>
                <input type="number" min={1} max={600} value={cfg.delay_seconds}
                  onChange={(e) => upd("delay_seconds", Number(e.target.value))}
                  data-testid="chatbot-delay" className="w-20 border border-black/15 px-2 py-1 rounded text-sm" />
                <span className="text-sm text-neutral-500">seg</span>
              </label>
            </div>
            <div className="mt-4">
              <label className="text-xs text-neutral-500">Não exibir nestas rotas (separado por vírgula)</label>
              <input value={(cfg.hide_on_paths || []).join(", ")} onChange={(e) => updPaths(e.target.value)}
                data-testid="chatbot-hide-paths"
                className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" />
              <div className="text-[11px] text-neutral-400 mt-1">O balão não aparece em URLs que começarem por qualquer um dos caminhos listados.</div>
            </div>
          </section>

          {/* Atendente */}
          <section className="border border-black/10 rounded-md p-5">
            <div className="flex items-center gap-2 mb-3">
              <Bot className="w-4 h-4 text-accent-orange" />
              <div className="text-xs uppercase tracking-widest text-accent-orange font-bold">Atendente virtual</div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-neutral-500">Nome</label>
                <input value={cfg.attendant_name} onChange={(e) => upd("attendant_name", e.target.value)}
                  data-testid="chatbot-attendant-name"
                  className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-neutral-500">Cargo / função</label>
                <input value={cfg.attendant_role} onChange={(e) => upd("attendant_role", e.target.value)}
                  data-testid="chatbot-attendant-role"
                  className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs text-neutral-500">Avatar (URL ou upload)</label>
              <div className="flex items-center gap-3 mt-1">
                {cfg.attendant_avatar && <img src={resolveSrc(cfg.attendant_avatar)} alt="" className="w-14 h-14 rounded-full object-cover border border-black/10" />}
                <input value={cfg.attendant_avatar} onChange={(e) => upd("attendant_avatar", e.target.value)}
                  data-testid="chatbot-attendant-avatar"
                  placeholder="Deixe vazio para usar iniciais"
                  className="flex-1 border border-black/15 px-3 py-2 rounded text-sm font-mono" />
              </div>
              <div className="mt-2"><DropZone multiple={false} accept={{ "image/*": [] }} onUploaded={(f) => upd("attendant_avatar", f.url)} /></div>
            </div>
            <div className="mt-3">
              <label className="text-xs text-neutral-500">Mensagem do balão (aparece na bolha inicial)</label>
              <input value={cfg.greeting} onChange={(e) => upd("greeting", e.target.value)}
                data-testid="chatbot-greeting"
                className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
            </div>
            <div className="mt-3">
              <label className="text-xs text-neutral-500">Mensagem de abertura (dentro do chat)</label>
              <textarea value={cfg.intro_message} onChange={(e) => upd("intro_message", e.target.value)} rows={2}
                data-testid="chatbot-intro"
                className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
            </div>
          </section>

          {/* Script fixo */}
          <section className="border border-black/10 rounded-md p-5">
            <div className="flex justify-between items-center mb-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-accent-orange font-bold">Roteiro fixo</div>
                <div className="text-sm text-neutral-500">Perguntas iniciais antes de liberar a IA. Nome e telefone são obrigatórios para o WhatsApp.</div>
              </div>
              <button onClick={addScriptStep} data-testid="chatbot-add-step"
                className="text-xs bg-black text-white px-3 py-2 rounded-full font-semibold hover:bg-accent-yellow hover:text-black">
                <Plus className="w-3 h-3 inline mr-1" /> Nova pergunta
              </button>
            </div>
            <div className="space-y-3">
              {(cfg.script || []).map((s, i) => (
                <div key={i} className="border border-black/10 rounded-md p-4" data-testid={`chatbot-step-${i}`}>
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-xs uppercase tracking-widest text-neutral-500">Passo {i + 1}</div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => moveScriptStep(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                      <button onClick={() => moveScriptStep(i, 1)} disabled={i === cfg.script.length - 1} className="p-1 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                      <button onClick={() => removeScriptStep(i)} className="p-1 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-2">
                    <div className="md:col-span-2">
                      <label className="text-[11px] text-neutral-500">Pergunta (use {'{name}'} para citar o nome)</label>
                      <input value={s.question} onChange={(e) => updScriptStep(i, "question", e.target.value)}
                        data-testid={`chatbot-step-question-${i}`}
                        className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
                    </div>
                    <div>
                      <label className="text-[11px] text-neutral-500">Campo</label>
                      <select value={s.field} onChange={(e) => updScriptStep(i, "field", e.target.value)}
                        data-testid={`chatbot-step-field-${i}`}
                        className="w-full border border-black/15 px-3 py-2 rounded text-sm bg-white">
                        <option value="name">Nome</option>
                        <option value="phone">Telefone / WhatsApp</option>
                        <option value="interest">Interesse (opcional)</option>
                        <option value="custom">Livre (não salvo)</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-2">
                    <label className="text-[11px] text-neutral-500">Placeholder</label>
                    <input value={s.placeholder} onChange={(e) => updScriptStep(i, "placeholder", e.target.value)}
                      className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
                  </div>
                </div>
              ))}
              {(!cfg.script || cfg.script.length === 0) && (
                <div className="text-center border border-dashed border-black/15 rounded p-6 text-sm text-neutral-500">
                  Nenhuma pergunta ainda. Sem roteiro, o bot vai direto para a IA (não recomendado).
                </div>
              )}
            </div>
            <div className="mt-4">
              <label className="text-xs text-neutral-500">System prompt da IA (avançado — deixe vazio para o padrão)</label>
              <textarea value={cfg.system_prompt} onChange={(e) => upd("system_prompt", e.target.value)}
                data-testid="chatbot-system-prompt"
                rows={3} placeholder="Deixe vazio para usar o system prompt padrão da Marina."
                className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" />
            </div>
          </section>

          {/* WhatsApp */}
          <section className="border border-accent-orange/30 bg-orange-50/40 rounded-md p-5">
            <div className="flex items-center gap-2 mb-3">
              <Phone className="w-4 h-4 text-accent-orange" />
              <div className="text-xs uppercase tracking-widest text-accent-orange font-bold">WhatsApp — funil final</div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-neutral-500">Número (formato internacional, apenas dígitos)</label>
                <input value={cfg.whatsapp_number} onChange={(e) => upd("whatsapp_number", e.target.value.replace(/\D/g, ""))}
                  data-testid="chatbot-whatsapp-number"
                  placeholder="5511999999999"
                  className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" />
                <div className="text-[11px] text-neutral-500 mt-1">Ex.: BR 55 + DDD + número.</div>
              </div>
              <div>
                <label className="text-xs text-neutral-500">Rótulo do botão</label>
                <input value={cfg.cta_label} onChange={(e) => upd("cta_label", e.target.value)}
                  data-testid="chatbot-cta-label"
                  className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs text-neutral-500">Mensagem pré-preenchida no WhatsApp (use {'{name}'}, {'{phone}'}, {'{interest}'}, {'{page}'})</label>
              <textarea value={cfg.whatsapp_message_template} onChange={(e) => upd("whatsapp_message_template", e.target.value)}
                data-testid="chatbot-whatsapp-msg"
                rows={3} className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" />
            </div>
            <div className="mt-3">
              <label className="text-xs text-neutral-500">Após quantas trocas de mensagens o botão aparece</label>
              <input type="number" min={1} max={20} value={cfg.cta_after_turns}
                onChange={(e) => upd("cta_after_turns", Number(e.target.value))}
                data-testid="chatbot-cta-after"
                className="w-24 border border-black/15 px-3 py-2 rounded text-sm" />
              <span className="text-[11px] text-neutral-500 ml-2">O botão sempre aparece após o roteiro fixo. Este contador conta as respostas da IA.</span>
            </div>
          </section>
        </div>

        {/* Preview */}
        <aside className="space-y-3">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Prévia do balão</div>
          <div className="bg-neutral-100 rounded-lg p-6 flex flex-col items-end gap-3">
            <div className="bg-white rounded-2xl shadow-lg border border-black/10 p-3 max-w-[240px] relative">
              <div className="text-xs font-semibold">{cfg.attendant_name}</div>
              <div className="text-[11px] text-neutral-500">{cfg.attendant_role}</div>
              <div className="text-sm mt-2">{cfg.greeting}</div>
              <div className="absolute -bottom-1 right-6 w-3 h-3 bg-white border-r border-b border-black/10 rotate-45" />
            </div>
            <div className="w-14 h-14 rounded-full bg-accent-yellow shadow-lg flex items-center justify-center relative">
              {cfg.attendant_avatar
                ? <img src={resolveSrc(cfg.attendant_avatar)} alt="" className="w-full h-full rounded-full object-cover" />
                : <span className="font-display text-xl text-black">{(cfg.attendant_name || "?").slice(0, 1)}</span>}
              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
            </div>
          </div>
          <div className="text-[11px] text-neutral-500">Não é interativo — só demonstração visual.</div>
        </aside>
      </div>
    </div>
  );
}
